import {
  DataMessageTypeEnum,
  type TerraUIMessagePart,
} from '@terra/shared-dto-chat-types';
import type { ProcessedMessage, ProcessedPart } from './message-processing';

const PLAN_INTRO = /here'?s the plan\b/i;
const PLAN_EMDASH_TITLE = /\bplan\s+[—–-]\s+\S/i;
const STEPS_HEADING = /(?:^|\n)\s*(?:\*{0,2}|#{1,3}\s*)steps\b/i;
const NUMBERED_STEP = /^\s*\d+\.\s+\S+/gm;

export function isCopilotPlanRecapText(text: string): boolean {
  const trimmed = text.trim();

  if (!trimmed) {
    return false;
  }

  if (
    PLAN_INTRO.test(trimmed) ||
    PLAN_EMDASH_TITLE.test(trimmed) ||
    STEPS_HEADING.test(trimmed)
  ) {
    return true;
  }

  const numbered = trimmed.match(NUMBERED_STEP);

  return Boolean(numbered && numbered.length >= 2 && /\bplan\b/i.test(trimmed));
}

function readAssistantText(part: ProcessedPart): string {
  if (part.kind !== 'assistant-text') {
    return '';
  }

  const messagePart: TerraUIMessagePart = part.part;

  if (messagePart.type !== 'text') {
    return '';
  }

  return messagePart.text;
}

function messageHasCopilotPlan(message: ProcessedMessage): boolean {
  return message.parts.some(
    (part) =>
      part.kind === 'data-message' &&
      part.dataMessage?.type === DataMessageTypeEnum.CopilotPlan,
  );
}

function skipPlanRecapParts(message: ProcessedMessage): ProcessedMessage {
  let changed = false;
  const parts = message.parts.map((part) => {
    if (part.kind !== 'assistant-text') {
      return part;
    }

    if (!isCopilotPlanRecapText(readAssistantText(part))) {
      return part;
    }

    changed = true;

    return { ...part, kind: 'skip' as const };
  });

  return changed ? { ...message, parts } : message;
}

export function suppressPinnedPlanArtifacts(
  messages: ProcessedMessage[],
): ProcessedMessage[] {
  return messages.map((message) => {
    if (message.message.role !== 'assistant') {
      return message;
    }

    let changed = false;
    const parts = message.parts.map((part) => {
      if (
        part.kind === 'data-message' &&
        part.dataMessage?.type === DataMessageTypeEnum.Todos
      ) {
        changed = true;

        return { ...part, kind: 'skip' as const };
      }

      return part;
    });

    return changed ? { ...message, parts } : message;
  });
}

export function suppressCopilotPlanRecapParts(
  messages: ProcessedMessage[],
): ProcessedMessage[] {
  const hasNearbyPlan = messages.map((message, index) => {
    if (messageHasCopilotPlan(message)) {
      return true;
    }

    const previous = index > 0 ? messages[index - 1] : undefined;
    const next = index + 1 < messages.length ? messages[index + 1] : undefined;

    return (
      (previous !== undefined && messageHasCopilotPlan(previous)) ||
      (next !== undefined && messageHasCopilotPlan(next))
    );
  });

  return messages.map((message, index) => {
    if (!hasNearbyPlan[index] || message.message.role !== 'assistant') {
      return message;
    }

    return skipPlanRecapParts(message);
  });
}
